// WindowFrame.jsx
import React, { useEffect, useState, useRef } from "react";
import Draggable from "react-draggable";
import OSIcon32 from "../../icons/OSIcon32";

export default function WindowFrame({
  title,
  children,
  onClose,
  width = 600,
  height = 600,
  center = false,
  initialPosition,
  isLPPortal = false,
  noPadding = false,
  windowId,
  zIndex = 1000,
  bringToFront,
  cascadeOffset = 0,
  icon,               // OSIcon32 kind rendered at 15px in the title bar
  titlebarVariant,    // "grape" | "aqua" | "tangerine" — magenta default
  minimized = false,  // window hides but stays mounted; taskbar restores it
  onMinimize          // rendered as a minimise button only when provided
}) {
  const nodeRef = useRef(null);
  const [mountState, setMountState] = useState(undefined);
  // Drag position is controlled so maximise can move the window to 0,0 and
  // restore can put it back exactly where it was dragged to.
  const [dragPos, setDragPos] = useState(null);
  const [maximized, setMaximized] = useState(false);
  // Small viewports lock every window to the full area above the taskbar —
  // no dragging, no restore-down. Keyed to the same 768px line the rest of
  // the app treats as "phone-sized".
  const [smallViewport, setSmallViewport] = useState(
    () => window.matchMedia("(max-width: 768px)").matches
  );

  // Reserved chrome: the 34px taskbar pinned to the bottom of the desktop.
  // Windows clamp to the space above it.
  const TASKBAR_RESERVE = 34;
  const TOP_PAD = 12;
  const SIDE_PAD = 16;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setSmallViewport(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Re-clamp on every viewport resize. Size and position are computed at
  // mount, so without this a window that mounts in a wide browser stays
  // 1200px wide at a centred-for-wide x when the viewport later narrows
  // (DevTools docking, window snapping, device-toolbar toggles) and ends up
  // stranded off the right edge. Resize pulls it back on-screen and lets it
  // grow back when the viewport widens; drag positions are otherwise kept.
  useEffect(() => {
    const onResize = () => {
      const maxW = Math.max(320, window.innerWidth - SIDE_PAD * 2);
      const maxH = Math.max(320, window.innerHeight - TOP_PAD - TASKBAR_RESERVE);
      const effW = Math.min(width, maxW);
      const effH = Math.min(height, maxH);
      setMountState((prev) => {
        if (!prev || (prev.w === effW && prev.h === effH)) return prev;
        return { ...prev, w: effW, h: effH };
      });
      setDragPos((prev) => {
        if (!prev) return prev;
        const maxX = Math.max(0, window.innerWidth - effW - SIDE_PAD);
        const maxY = Math.max(TOP_PAD, window.innerHeight - effH - TASKBAR_RESERVE);
        const x = Math.min(Math.max(0, prev.x), maxX);
        const y = Math.min(Math.max(0, prev.y), maxY);
        if (x === prev.x && y === prev.y) return prev;
        return { x, y };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [width, height]);

  // Calculate size + position ONCE per mount — deliberately no deps. Callers
  // pass initialPosition as an inline object literal (new identity every
  // parent render), so depending on it refired this effect on every desktop
  // re-render (the z-order bump when a drag-release clicks the window, clock
  // ticks, message snapshots) and reset dragPos — Buddy Messenger snapped
  // back to its mount position after every drag. Width/height/cascadeOffset
  // are likewise mount-time by design; post-mount geometry is the resize
  // clamp effect's job.
  useEffect(() => {
    const maxW = Math.max(320, window.innerWidth - SIDE_PAD * 2);
    const maxH = Math.max(320, window.innerHeight - TOP_PAD - TASKBAR_RESERVE);
    const effW = Math.min(width, maxW);
    const effH = Math.min(height, maxH);

    const maxX = Math.max(0, window.innerWidth - effW - SIDE_PAD);
    const maxY = Math.max(TOP_PAD, window.innerHeight - effH - TASKBAR_RESERVE);

    let pos;
    if (initialPosition) {
      pos = {
        x: Math.min(maxX, Math.max(SIDE_PAD, initialPosition.x + cascadeOffset)),
        y: Math.min(maxY, Math.max(TOP_PAD, initialPosition.y + cascadeOffset))
      };
    } else if (center) {
      const availableH = window.innerHeight - TOP_PAD - TASKBAR_RESERVE;
      const baseX = Math.max(SIDE_PAD, Math.round((window.innerWidth - effW) / 2));
      const baseY = TOP_PAD + Math.max(0, Math.round((availableH - effH) / 2));
      pos = {
        x: Math.min(maxX, baseX + cascadeOffset),
        y: Math.min(maxY, baseY + cascadeOffset)
      };
    } else {
      pos = {
        x: Math.min(maxX, 100 + cascadeOffset),
        y: Math.min(maxY, Math.max(TOP_PAD, 100 + cascadeOffset))
      };
    }

    setMountState({ w: effW, h: effH, pos });
    setDragPos(pos);
  }, []);

  // Handler for window click to bring it to front
  const handleWindowClick = () => {
    if (bringToFront && windowId) {
      bringToFront(windowId);
    }
  };

  // Focus trap — cycles Tab / Shift+Tab within the window when focus is inside it.
  // Multiple open windows each run their own trap; only the one containing
  // the active element intercepts keystrokes, so windows don't fight each other.
  const handleKeyDown = (e) => {
    if (e.key !== "Tab") return;
    const root = nodeRef.current;
    if (!root || !root.contains(document.activeElement)) return;

    const focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Locked-full-size rendering: small viewports always, desktop when
  // maximised. CSS units (100vw / dvh) track browser-chrome and orientation
  // changes without a resize listener.
  const fillScreen = maximized || smallViewport;

  // Position/layout-only styles; visual chrome lives in .win95-window / .win95-titlebar
  const windowStyle = {
    width: fillScreen ? "100vw" : (mountState ? mountState.w : width),
    height: fillScreen
      ? "calc(100dvh - var(--taskbar-h, 34px))"
      : (mountState ? mountState.h : height),
    position: "absolute",
    top: 0,
    left: 0,
    display: minimized ? "none" : "flex",
    flexDirection: "column",
    zIndex: zIndex,
    overflow: "hidden"
  };

  const titleBarStyle = {
    flexShrink: 0
  };

  // Base body style for content area
  const baseBodyStyle = {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    position: "relative",
    minHeight: 0
  };

  // Special styles for LP Portal
  const lpPortalBodyOverrides = {
    padding: 0,
    overflowY: "hidden",
    display: "flex",
    minHeight: 0,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%"
  };

  // Flush-body override (e.g., browser window) — the child manages its own
  // padding and scroll, so the body must be padding-free and flex to let
  // the child's flex:1 fill the height.
  const noPaddingBodyOverrides = {
    padding: 0,
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column"
  };

  // Determine which body style to use
  let bodyStyle = baseBodyStyle;
  if (isLPPortal) {
    bodyStyle = { ...baseBodyStyle, ...lpPortalBodyOverrides };
  } else if (noPadding) {
    bodyStyle = { ...baseBodyStyle, ...noPaddingBodyOverrides };
  }

  // Determine body class name
  const bodyClassName = `retro-window-body win95-scroll no-drag ${isLPPortal ? "lpportal-body-styling" : ""}`;

  const titlebarClassName = [
    "retro-title-bar",
    "win95-titlebar",
    titlebarVariant ? `win95-titlebar--${titlebarVariant}` : ""
  ].filter(Boolean).join(" ");

  // Wait until size + position are calculated to avoid render jump
  if (mountState === undefined || dragPos === null) {
    return null;
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".retro-title-bar"
      cancel=".no-drag, .leaflet-container"
      position={fillScreen ? { x: 0, y: 0 } : dragPos}
      // Sync the controlled position on drag END only. Per-move setState here
      // races the library's internal per-mousemove state: React flushes lag a
      // few moves behind, getDerivedStateFromProps yanks the window back to
      // the stale prop mid-drag, and onDragStop reverts to it — windows
      // snapped back to where they started. With the prop frozen during the
      // drag the library moves the node freely, and the single onStop sync
      // lands in the same commit as its internal controlled-revert, so the
      // final position sticks. Maximise/restore still jump via prop changes.
      onStop={(e, data) => setDragPos({ x: data.x, y: data.y })}
      disabled={fillScreen}
    >
      <div
        ref={nodeRef}
        className="retro-window win95-window win95-window--frame"
        style={windowStyle}
        onClick={handleWindowClick}
        onKeyDown={handleKeyDown}
      >
        <div className={titlebarClassName} style={titleBarStyle}>
          {icon && (
            <OSIcon32 kind={icon} size={15} className="win95-titlebar__icon" />
          )}
          <span className="win95-titlebar__label">{title}</span>
          <div className="win95-titlebar__btns">
            {onMinimize && (
              <button
                className="no-drag win95-btn-sys"
                onClick={onMinimize}
                aria-label="Minimise"
                type="button"
              >
                <span className="win95-btn-sys__min" aria-hidden="true" />
              </button>
            )}
            {!smallViewport && (
              <button
                className="no-drag win95-btn-sys"
                onClick={() => setMaximized((m) => !m)}
                aria-label="Maximise"
                aria-pressed={maximized}
                type="button"
              >
                <span className="win95-btn-sys__max" aria-hidden="true" />
              </button>
            )}
            <button
              className="no-drag win95-btn-sys win95-close-btn"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>

        <div
          className={bodyClassName}
          style={bodyStyle}
        >
          {children}
        </div>
      </div>
    </Draggable>
  );
}
